import { intakeService } from "@/services/intakeService";
import { patientService } from "@/services/patientService";
import { ref } from "vue";
import { debounce, isEqual } from "lodash";
import { formatDateToString } from "@/utils/index";

/**
 * A mixin that adds dirty field tracking functionality to a class.
 * Tracks which fields have been modified since the last save/clear.
 */
const DirtyFieldsMixin = (superclass) =>
  class extends superclass {
    DATE_FIELDS = [];

    constructor(...args) {
      super(...args);
      this._dirtyFields = [];
    }

    /**
     * Updates a field value and marks it as dirty if changed
     * @param {string} fieldKey - The name of the field to update
     * @param {any} value - The new value to set
     */
    updateAttribute(fieldKey, value) {
      this[fieldKey] = value;
      if (!this._dirtyFields.includes(fieldKey)) {
        this._dirtyFields.push(fieldKey);
      }
    }

    /**
     * Gets the list of fields that have been modified
     * @returns {string[]} Array of field names that have been modified
     */
    get dirtyFields() {
      return this._dirtyFields;
    }

    /**
     * Clears the dirty fields list, optionally filtering based on a patch
     * @param {{fromPatch?: Record<string, any>}} [options] - Options for clearing dirty fields
     * @param {Record<string, any>} [options.fromPatch] - If provided, only clears fields that match the patch values
     */
    clearDirtyFields(options) {
      const { fromPatch } = options || {};
      if (fromPatch) {
        this._dirtyFields = this._dirtyFields.filter((field) => {
          let value = this[field];
          // date fields should be compared as dates
          if (this.DATE_FIELDS.includes(field) && value instanceof Date) {
            value = formatDateToString(value);
          }
          return !(fromPatch.hasOwnProperty(field) && isEqual(value, fromPatch[field]));
        });
      } else {
        this._dirtyFields = [];
      }
    }
  };

export class IntakeInsuranceCardData {
  DATE_FIELDS = [];
  parseExtraDataDates(extraData, dateAdapter) {
    const parsedExtraData = { ...extraData };

    this.DATE_FIELDS.forEach((field) => {
      if (parsedExtraData[field] && typeof parsedExtraData[field] === "string") {
        const parsed = dateAdapter.parseISO(parsedExtraData[field]);
        parsedExtraData[field] = parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
      }
    });

    return parsedExtraData;
  }

  constructor(card, dateAdapter) {
    this.dateAdapter = dateAdapter;
    this.insurancePlan = card.insurancePlan;
    this.phone1Number = card.phone1Number;
    this.phone1Description = card.phone1Description;
    this.phone2Number = card.phone2Number;
    this.phone2Description = card.phone2Description;
    this.fax1Number = card.fax1Number;
    this.fax1Description = card.fax1Description;
    this.memberName = card.memberName;
    this.memberId = card.memberId;
    this.memberDob = card.memberDob ? dateAdapter.parseISO(card.memberDob) : null;
    this.memberGender = card.memberGender;
    this.memberRelationship = card.memberRelationship;
    this.payorName = card.payorName;
    this.planType = card.planType;
    this.state = card.state;
    this.groupNumber = card.groupNumber;
    this.startDate = card.startDate;
    this.endDate = card.endDate;
    this.rxBin = card.rxBin;
    this.rxPcn = card.rxPcn;
    this.rxGrp = card.rxGrp;
    this.cardRole = card.cardRole;
    this.benefitType = card.benefitType;
    this.extraData = this.parseExtraDataDates(card.extraData ?? {}, dateAdapter);
    this.rawPages = card.pages;
    if (card.pages && card.pages.length > 0) {
      // Get the first page
      if (card.pages[0].insuranceCardsData && card.pages[0].insuranceCardsData.length > 0) {
        const firstCard = card.pages[0].insuranceCardsData[0];
        this.insurancePlanMetaData = firstCard.insurancePlanMetaData;
        this.phone1NumberMetaData = firstCard.phone1NumberMetaData;
        this.phone1DescriptionMetaData = firstCard.phone1DescriptionMetaData;
        this.phone2NumberMetaData = firstCard.phone2NumberMetaData;
        this.phone2DescriptionMetaData = firstCard.phone2DescriptionMetaData;
        this.fax1NumberMetaData = firstCard.fax1NumberMetaData;
        this.fax1DescriptionMetaData = firstCard.fax1DescriptionMetaData;
        this.memberNameMetaData = firstCard.memberNameMetaData;
        this.memberIdMetaData = firstCard.memberIdMetaData;
        this.memberDobMetaData = firstCard.memberDobMetaData;
        this.memberGenderMetaData = firstCard.memberGenderMetaData;
        this.payorNameMetaData = firstCard.payorNameMetaData;
        this.planTypeMetaData = firstCard.planTypeMetaData;
        this.stateMetaData = firstCard.stateMetaData;
        this.groupNumberMetaData = firstCard.groupNumberMetaData;
        this.startDateMetaData = firstCard.startDateMetaData;
        this.endDateMetaData = firstCard.endDateMetaData;
        this.rxBinMetaData = firstCard.rxBinMetaData;
        this.rxPcnMetaData = firstCard.rxPcnMetaData;
        this.rxGrpMetaData = firstCard.rxGrpMetaData;
        this.cardRoleMetaData = firstCard.cardRoleMetaData;
        this.benefitTypeMetaData = firstCard.benefitTypeMetaData;
      }
    }
  }
}
export class IntakeInsuranceCard extends DirtyFieldsMixin(IntakeInsuranceCardData) {
  DATE_FIELDS = ["memberDob"];

  constructor(card, dateAdapter) {
    super(card, dateAdapter);
    this.createdOn = card.createdOn ? new Date(card.createdOn) : null;
    this.modifiedOn = card.modifiedOn ? new Date(card.modifiedOn) : null;
    this.id = card.id;
    this.uiId = `${this.id}-${Math.random()}`;
    this.pages = (card.pages || []).map((page) => new IntakeDocumentPage(page, dateAdapter));
    this.hasUIErrors = false;

    this.debouncedSaveUpdates = debounce(this.saveUpdates, 2000);
  }

  saveUpdates() {
    const patch = {};
    this.dirtyFields.forEach((field) => {
      patch[field] = this[field];
    });
    if (patch.memberDob instanceof Date) {
      patch.memberDob = formatDateToString(patch.memberDob);
    }

    if (patch.extraData) {
      patch.extraData = { ...patch.extraData };
      this.DATE_FIELDS.forEach((field) => {
        if (patch.extraData[field] instanceof Date) {
          patch.extraData[field] = formatDateToString(patch.extraData[field]);
        }
      });
    }
    const p = this.dirtyFields.length ? intakeService.patchInsuranceCard(this.id, patch) : Promise.resolve();
    p.then(() => {
      this.clearDirtyFields({ fromPatch: patch });
    });
    return p;
  }
}

export class IntakeOrderFormData {
  parseExtraDataDates(extraData, dateAdapter) {
    const parsedExtraData = { ...extraData };

    const dateFields = ["dateOfOrder", "dateOfExpiration", "effectiveDate", "startOfCare", "referralDate"];

    dateFields.forEach((field) => {
      if (parsedExtraData[field] && typeof parsedExtraData[field] === "string") {
        const parsed = dateAdapter.parseISO(parsedExtraData[field]);
        parsedExtraData[field] = parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
      }
    });

    return parsedExtraData;
  }

  constructor(form, dateAdapter) {
    this.dateAdapter = dateAdapter;
    this.patientFirstName = form.patientFirstName;
    this.patientLastName = form.patientLastName;
    this.patientDob = form.patientDob ? dateAdapter.parseISO(form.patientDob) : null;
    this.patientPhone = form.patientPhone;
    this.patientEmail = form.patientEmail;

    // Patient demographics
    this.patientGender = form.patientGender;
    this.patientAddress = form.patientAddress;
    this.patientCity = form.patientCity;
    this.patientState = form.patientState;
    this.patientZip = form.patientZip;

    this.patientWeightValue = form.patientWeightValue;
    this.patientWeightUnit = form.patientWeightUnit;
    this.patientStatus = form.patientStatus;
    this.patientAllergies = form.patientAllergies;
    this.diagnosisIcd10Code = form.diagnosisIcd10Code;
    this.providerFullName = form.providerFullName;
    this.providerFirstName = form.providerFirstName;
    this.providerLastName = form.providerLastName;
    this.providerTitle = form.providerTitle;
    this.providerNpi = form.providerNpi;
    this.providerAddress = form.providerAddress;
    this.providerCity = form.providerCity;
    this.providerState = form.providerState;
    this.providerZip = form.providerZip;
    this.providerPracticeName = form.providerPracticeName;
    this.providerPhone = form.providerPhone;
    this.providerPhoneExtension = form.providerPhoneExtension;
    this.providerFax = form.providerFax;
    this.providerFaxExtension = form.providerFaxExtension;
    this.preMedication = form.preMedication ?? [];
    if (form.therapyAdministration) {
      this.therapyAdministration = form.therapyAdministration.map((therapy) => {
        if (therapy.orderDate && typeof therapy.orderDate === "string") {
          return {
            ...therapy,
            orderDate: dateAdapter.parseISO(therapy.orderDate),
          };
        }
        return therapy;
      });
    } else {
      this.therapyAdministration = [];
    }
    this.otherCheckboxes = form.otherCheckboxes ?? [];
    this.specialInstructions = form.specialInstructions;
    this.patientFacility = form.patientFacility;
    this.intakeDocumentKey = form.intakeDocumentKey;
    this.dateOfOrder = form.dateOfOrder ? dateAdapter.parseISO(form.dateOfOrder) : null;
    this.dateOfExpiration = form.dateOfExpiration ? dateAdapter.parseISO(form.dateOfExpiration) : null;
    this.extraDiagnosisCodes = form.extraDiagnosisCodes ?? [];
    this.extraData = this.parseExtraDataDates(form.extraData ?? {}, dateAdapter);
    this.uiState = form.uiState ?? {};

    if (form.pages && form.pages.length > 0) {
      const firstPage = form.pages[0];
      if (firstPage && firstPage.orderFormData) {
        this.patientFirstNameMetaData = firstPage.orderFormData.patientFirstNameMetaData;
        this.patientLastNameMetaData = firstPage.orderFormData.patientLastNameMetaData;
        this.patientDobMetaData = firstPage.orderFormData.patientDobMetaData;
        this.patientPhoneMetaData = firstPage.orderFormData.patientPhoneMetaData;
        this.patientGenderMetaData = firstPage.orderFormData.patientGenderMetaData;
        this.patientAddressMetaData = firstPage.orderFormData.patientAddressMetaData;
        this.patientCityMetaData = firstPage.orderFormData.patientCityMetaData;
        this.patientStateMetaData = firstPage.orderFormData.patientStateMetaData;
        this.patientZipMetaData = firstPage.orderFormData.patientZipMetaData;
        this.patientWeightValueMetaData = firstPage.orderFormData.patientWeightValueMetaData;
        this.patientWeightUnitMetaData = firstPage.orderFormData.patientWeightUnitMetaData;
        this.patientStatusMetaData = firstPage.orderFormData.patientStatusMetaData;
        this.patientAllergiesMetaData = firstPage.orderFormData.patientAllergiesMetaData;
        this.diagnosisIcd10CodeMetaData = firstPage.orderFormData.diagnosisIcd10CodeMetaData;
        this.providerFullNameMetaData = firstPage.orderFormData.providerFullNameMetaData;
        this.providerFirstNameMetaData = firstPage.orderFormData.providerFirstNameMetaData;
        this.providerLastNameMetaData = firstPage.orderFormData.providerLastNameMetaData;
        this.providerTitleMetaData = firstPage.orderFormData.providerTitleMetaData;
        this.providerNpiMetaData = firstPage.orderFormData.providerNpiMetaData;
        this.providerAddressMetaData = firstPage.orderFormData.providerAddressMetaData;
        this.providerCityMetaData = firstPage.orderFormData.providerCityMetaData;
        this.providerStateMetaData = firstPage.orderFormData.providerStateMetaData;
        this.providerZipMetaData = firstPage.orderFormData.providerZipMetaData;
        this.providerPracticeNameMetaData = firstPage.orderFormData.providerPracticeNameMetaData;
        this.providerPhoneMetaData = firstPage.orderFormData.providerPhoneMetaData;
        this.providerFaxMetaData = firstPage.orderFormData.providerFaxMetaData;
        this.preMedicationMetaData = firstPage.orderFormData.preMedicationMetaData;
        this.therapyAdministrationMetaData = firstPage.orderFormData.therapyAdministrationMetaData;
        this.otherCheckboxesMetaData = firstPage.orderFormData.otherCheckboxesMetaData;
        this.specialInstructionsMetaData = firstPage.orderFormData.specialInstructionsMetaData;
        this.patientFacilityMetaData = firstPage.orderFormData.patientFacilityMetaData;
        this.dateOfOrderMetaData = firstPage.orderFormData.dateOfOrderMetaData;
        this.dateOfExpirationMetaData = firstPage.orderFormData.dateOfExpirationMetaData;
        this.extraDiagnosisCodesMetaData = firstPage.orderFormData.extraDiagnosisCodesMetaData;
        this.patientHeightMetaData = firstPage.orderFormData.patientHeightMetaData;
        this.patientHeightUnitMetaData = firstPage.orderFormData.patientHeightUnitMetaData;
      }
    }
  }
}

export class IntakeOrderForm extends DirtyFieldsMixin(IntakeOrderFormData) {
  constructor(form, dateAdapter) {
    super(form, dateAdapter);
    this.createdOn = form.createdOn ? new Date(form.createdOn) : null;
    this.modifiedOn = form.modifiedOn ? new Date(form.modifiedOn) : null;
    this.id = form.id;
    this.uiId = `${this.id}-${Math.random()}`;
    this.pages = (form.pages || []).map((page) => new IntakeDocumentPage(page, dateAdapter));
    this.hasUIErrors = false;
    this.patientInfoHasUIErrors = false;
    this.providerInfoHasUIErrors = false;
    this.medicalDetailsHasUIErrors = false;
    this.debouncedSaveUpdates = debounce(this.saveUpdates, 2000);
  }

  saveUpdates() {
    const patch = {};
    this.dirtyFields.forEach((field) => {
      patch[field] = this[field];
    });
    if (patch.dateOfOrder instanceof Date) {
      patch.dateOfOrder = formatDateToString(patch.dateOfOrder);
    }
    if (patch.dateOfExpiration instanceof Date) {
      patch.dateOfExpiration = formatDateToString(patch.dateOfExpiration);
    }
    if (patch.therapyAdministration) {
      patch.therapyAdministration = patch.therapyAdministration.map((therapy) => {
        if (therapy.orderDate instanceof Date) {
          return {
            ...therapy,
            orderDate: formatDateToString(therapy.orderDate),
          };
        }
        return therapy;
      });
    }
    if (patch.extraData) {
      patch.extraData = { ...patch.extraData };
      const dateFields = ["dateOfOrder", "dateOfExpiration", "effectiveDate", "startOfCare", "referralDate"];
      dateFields.forEach((field) => {
        if (patch.extraData[field] instanceof Date) {
          patch.extraData[field] = formatDateToString(patch.extraData[field]);
        }
      });
    }
    const p = this.dirtyFields.length ? intakeService.patchOrderForm(this.id, patch) : Promise.resolve();
    p.then(() => {
      this.clearDirtyFields({ fromPatch: patch });
    });
    return p;
  }
}

export class IntakeToDoData {
  constructor(todo, dateAdapter) {
    this.dateAdapter = dateAdapter;
    this.toDo = todo.toDo;
    this.dueDate = todo.dueDate ? dateAdapter.parseISO(todo.dueDate) : null;
    this.priority = todo.priority;
    this.department = todo.department;
    this.type = todo.type;
    this.assignedTo = todo.assignedTo ?? [];
    this.notes = todo.notes;
    this.progressNotesTemplate = todo.progressNotesTemplate;
  }
}

export class IntakeToDo extends DirtyFieldsMixin(IntakeToDoData) {
  constructor(toDo, dateAdapter) {
    super(toDo, dateAdapter);
    this.createdOn = toDo.createdOn ? new Date(toDo.createdOn) : null;
    this.modifiedOn = toDo.modifiedOn ? new Date(toDo.modifiedOn) : null;
    this.id = toDo.id;
    this.uiId = `${this.id}-${Math.random()}`;
    this.pages = (toDo.pages || []).map((page) => new IntakeDocumentPage(page, dateAdapter));
    this.hasUIErrors = false;
    this.debouncedSaveUpdates = debounce(this.saveUpdates, 2000);
  }

  saveUpdates() {
    const patch = {};
    this.dirtyFields.forEach((field) => {
      patch[field] = this[field];
    });
    if (patch.dueDate instanceof Date) {
      patch.dueDate = formatDateToString(patch.dueDate);
    }
    const p = this.dirtyFields.length ? intakeService.patchToDo(this.id, patch) : Promise.resolve();
    p.then(() => {
      this.clearDirtyFields({ fromPatch: patch });
    });
    return p;
  }
}

export class IntakeDocument {
  constructor(document, dateAdapter) {
    this.id = document.id;
    this.createdOn = document.createdOn ? new Date(document.createdOn) : null;
    this.modifiedOn = document.modifiedOn ? new Date(document.modifiedOn) : null;
    this.status = document.status;
    this.statusChangedAt = document.statusChangedAt ? new Date(document.statusChangedAt) : null;
    this.documentKey = document.key;
    this.snoozeUntil = document.snoozeUntil ? new Date(document.snoozeUntil) : null;
    this.isSnoozed = this.snoozeUntil ? this.snoozeUntil > new Date() : false;
    this.intakeInsuranceCards = document.intakeInsuranceCards?.map(
      (card) => new IntakeInsuranceCard(card, dateAdapter)
    );
    this.intakeOrderForm = document.intakeOrderForms[0]
      ? new IntakeOrderForm(document.intakeOrderForms[0], dateAdapter)
      : null;
    this.intakeToDos = document.intakeToDos?.map((toDo) => new IntakeToDo(toDo, dateAdapter));
    this.patientGuess = document.patientGuess;
    this.patient = document.patient;
    this.clientCompany = document.clientCompany;
    this.inputPresignedUrl = document.inputPresignedUrl;
    this.lock = document.lock;
    this.archivedReason = document.archivedReason;
    this.statusTags = document.statusTags || [];
    this.assignees = document.assignees || [];

    this.debouncedSaveUpdates = debounce(this.saveUpdates, 2000);
  }

  updateAttribute(fieldKey, value, type, id) {
    if (type === "ORDER_FORM") {
      this.intakeOrderForm?.updateAttribute(fieldKey, value);
    } else if (type === "INSURANCE_CARD") {
      const card = this.intakeInsuranceCards.find((card) => card.id === id);
      if (card) {
        card.updateAttribute(fieldKey, value);
      }
    } else if (type === "TO_DO") {
      const toDo = this.intakeToDos.find((toDo) => toDo.id === id);
      if (toDo) {
        toDo.updateAttribute(fieldKey, value);
      }
    }
  }

  saveUpdates() {
    const promises = [];
    if (this.intakeOrderForm) {
      promises.push(this.intakeOrderForm.saveUpdates());
    }
    if (this.intakeInsuranceCards) {
      promises.push(this.intakeInsuranceCards.map((card) => card.saveUpdates()));
    }
    if (this.intakeToDos) {
      promises.push(this.intakeToDos.map((toDo) => toDo.saveUpdates()));
    }
    return Promise.all(promises);
  }
}

export class IntakeDocumentPage extends DirtyFieldsMixin(Object) {
  constructor(page, dateAdapter) {
    super(page);
    this.id = page.id;
    this.documentKey = page.documentKey;
    this.imagePresignedUrl = page.imagePresignedUrl;
    this.documentType = page.documentType;
    this.pageNumber = page.pageNumber;
    this.intakeInsuranceCardsData = page.intakeInsuranceCardsData
      ? page.intakeInsuranceCardsData.map((card) => new IntakeInsuranceCardData(card))
      : [];
    this.intakeOrderFormData = page.intakeOrderFormData ? new IntakeOrderFormData(page.intakeOrderFormData) : null;
    this.otherData = page.otherData;
    this.descriptor = page.descriptor;
    // A unique id for the page object in the UI, used to differentiate between the same page with multiple cards
    this.uiId = `${page.id}-${Math.random()}`;
    this.dueDate = page.dueDate ? dateAdapter.parseISO(page.dueDate) : null;
    this.uiState = page.uiState ?? {};

    this.debouncedSaveUpdates = debounce(this.saveUpdates, 2000);
  }

  get rotationDegrees() {
    return this.uiState?.rotationDegrees ?? 0;
  }

  set rotationDegrees(degrees) {
    this.uiState = { ...this.uiState, rotationDegrees: degrees };
    this.updateAttribute("uiState", this.uiState);
  }

  saveUpdates() {
    const patch = {};
    this.dirtyFields.forEach((field) => {
      patch[field] = this[field];
    });
    if (patch.dueDate instanceof Date) {
      patch.dueDate = formatDateToString(patch.dueDate);
    }
    const p = this.dirtyFields.length
      ? intakeService.patchIntakeDocumentPage(this.documentKey, this.id, patch)
      : Promise.resolve();
    p.then(() => {
      this.clearDirtyFields({ fromPatch: patch });
    });
    return p;
  }
}

export class IntakeOrder extends DirtyFieldsMixin(Object) {
  constructor(order, dateAdapter) {
    super(order);
    this.id = order.id;
    this.modifiedOn = order.modifiedOn ? new Date(order.modifiedOn) : null;
    this.createdOn = order.createdOn ? new Date(order.createdOn) : null;
    this.statusChangedAt = order.statusChangedAt ? new Date(order.statusChangedAt) : null;
    this.snoozeUntil = order.snoozeUntil ? new Date(order.snoozeUntil) : null;
    this.isSnoozed = this.snoozeUntil ? this.snoozeUntil > new Date() : false;
    if (order.patient) {
      this.patient = new Patient(order.patient, dateAdapter);
    }
    if (order.insuranceCard) {
      this.insuranceCard = new IntakeInsuranceCard(order.insuranceCard, dateAdapter);
    }
    if (order.orderForm) {
      this.orderForm = new IntakeOrderForm(order.orderForm, dateAdapter);
    }
    this.status = order.status;
    this.policyReview = order.policyReview;
    this.policyChecklist = order.policyChecklist || [];
    this.pageTags = order.pageTags;
    this.clientCompany = order.clientCompany;
    this.payorNames = order.payorNames;
    this.archivedReason = order.archivedReason;
    this.statusTags = order.statusTags || [];
    this.checklistGroups = {};
    this.policySummary = order.policySummary ?? "";
    this.orderFormDocumentCreatedOn = order.orderFormDocumentCreatedOn
      ? new Date(order.orderFormDocumentCreatedOn)
      : null;
    this.isQuickStartSubmission = !!order.isQuickStartSubmission;
    this.generateChecklistGroups();
    this.debouncedSaveUpdates = debounce(this.saveUpdates, 2000);
  }

  generateChecklistGroups() {
    this.checklistGroups = this.policyChecklist.reduce((acc, item) => {
      acc[item.category] = acc[item.category] || {};
      acc[item.category][item.subCategory] = acc[item.category][item.subCategory] || [];
      acc[item.category][item.subCategory].push(item);
      return acc;
    }, {});
  }

  updateAttribute(fieldKey, value) {
    super.updateAttribute(fieldKey, value);
    if (fieldKey === "policyChecklist") {
      this.generateChecklistGroups();
    }
  }

  saveUpdates() {
    const promises = [];
    if (this.insuranceCard) {
      promises.push(this.insuranceCard.saveUpdates());
    }
    if (this.orderForm) {
      promises.push(this.orderForm.saveUpdates());
    }
    const patch = {};
    this.dirtyFields.forEach((field) => {
      patch[field] = this[field];
    });
    if (Object.keys(patch).length > 0) {
      promises.push(
        intakeService.patchIntakeOrder(this.id, patch).then(() => {
          this.clearDirtyFields({ fromPatch: patch });
        })
      );
    }
    return Promise.all(promises);
  }
}

class PatientData {
  constructor(patient, dateAdapter) {
    const p = patient || {};
    this.dateAdapter = dateAdapter;
    this.createdOn = p.createdOn;
    this.modifiedOn = p.modifiedOn;
    this.firstName = p.firstName ?? "";
    this.lastName = p.lastName ?? "";
    this.middleName = p.middleName ?? "";
    this.suffix = p.suffix ?? "";
    this.phoneNumber = p.phoneNumber ?? "";
    const parsedDob = p.dob ? dateAdapter.parseISO(p.dob) : null;
    this.dob = parsedDob instanceof Date && !Number.isNaN(parsedDob.getTime()) ? parsedDob : null;
    this.dobStr = p.dob;
    this.id = p.id;
    this.clientCompany = p.clientCompany;
    this.hasInsuranceCard = p.hasInsuranceCard;
    this.extraData = p.extraData ?? {};
    this.address = p.address ?? "";
    this.city = p.city ?? "";
    this.state = p.state ?? "";
    this.zip = p.zip ?? "";
    this.gender = p.gender ?? "";
    this.externalId = p.externalId ?? "";
  }
}

/** Tenant patient record with dirty tracking and debounced PATCH (mirrors IntakeOrderForm / IntakeInsuranceCard). */
export class Patient extends DirtyFieldsMixin(PatientData) {
  DATE_FIELDS = ["dob"];

  constructor(patient, dateAdapter) {
    super(patient, dateAdapter);
    this.debouncedSaveUpdates = debounce(this.saveUpdates, 2000);
  }

  updateAttribute(fieldKey, value) {
    if (fieldKey === "extraData") {
      this.extraData = value !== null && typeof value === "object" ? { ...value } : {};
      if (!this._dirtyFields.includes(fieldKey)) {
        this._dirtyFields.push(fieldKey);
      }
      return;
    }
    super.updateAttribute(fieldKey, value);
  }

  saveUpdates() {
    if (!this.id) {
      return Promise.resolve();
    }
    const patchKeys = this.dirtyFields;
    if (!patchKeys.length) {
      return Promise.resolve();
    }
    const patch = {};
    patchKeys.forEach((field) => {
      if (this.DATE_FIELDS.includes(field)) {
        patch[field] = formatDateToString(this[field]);
      } else {
        patch[field] = this[field];
      }
    });
    const p = patientService.patchPatient(this.id, patch);
    p.then(() => {
      this.clearDirtyFields({ fromPatch: patch, dateAdapter: this.dateAdapter });
    });
    return p;
  }
}

// Status / classification enums live in `intakeStatus.ts` so they can be
// consumed by typed code too. Re-exported here for existing callers.
export { INTAKE_STAGE, DOCUMENT_STAGE, DOCUMENT_CLASSIFICATIONS } from "@/models/intakeStatus";

export class PageChangeManager {
  constructor(pages) {
    this.currentPageIndex = 0;
    this._pages = pages;
    // null = no filter (full page-order stepping); "" = filter on the Unclassified bucket;
    // else a documentType string. Always compare with `=== null`, never truthiness, so the
    // Unclassified ("") filter is not mistaken for "no filter".
    this.activeFilter = null;
    this.currentPageRef = ref(this._pages[this.currentPageIndex]);
  }

  get currentPage() {
    return this.currentPageRef;
  }

  set pages(pages) {
    this._pages = pages;
    this.currentPageIndex = 0;
    this.activeFilter = null;
    this.currentPageRef = this._pages[this.currentPageIndex];
  }

  get pages() {
    return this._pages;
  }

  getPageNumber(pageId) {
    return this._pages.findIndex((page) => page.id === pageId);
  }

  // Move the current page by array index without touching the active filter.
  // Used by filter-aware navigation; distinct from goToPage(), which exits scoped mode.
  _setIndex(num) {
    if (num >= 0 && num < this._pages.length) {
      this.currentPageIndex = num;
      this.currentPageRef = this._pages[this.currentPageIndex];
    }
  }

  // Indices of pages matching the active filter, in page order. Empty when unfiltered.
  get filteredIndices() {
    if (this.activeFilter === null) {
      return [];
    }
    return this._pages.reduce((acc, page, ix) => {
      if ((page.documentType || "") === this.activeFilter) {
        acc.push(ix);
      }
      return acc;
    }, []);
  }

  // Total pages in the active filter (for the "X of N" scoped counter).
  get scopedTotal() {
    return this.filteredIndices.length;
  }

  // 1-based position of the current page within the active filter (0 if not in it).
  get scopedPosition() {
    const pos = this.filteredIndices.indexOf(this.currentPageIndex);
    return pos === -1 ? 0 : pos + 1;
  }

  // Document-type index for the TOC: one entry per unique type (merged across
  // non-contiguous occurrences), in first-appearance order, with contiguous
  // list-position spans. Labels are formatted by the consuming component.
  get documentTypeGroups() {
    const order = [];
    const byType = new Map();
    this._pages.forEach((page, ix) => {
      const type = page.documentType || "";
      if (!byType.has(type)) {
        byType.set(type, []);
        order.push(type);
      }
      byType.get(type).push(ix);
    });
    return order.map((type) => {
      const indices = byType.get(type);
      return { type, indices, spans: PageChangeManager._spansFromIndices(indices) };
    });
  }

  // Collapse indices into contiguous list-position runs, e.g. [0,1,2,62] -> "p1–p3, p63".
  static _spansFromIndices(indices) {
    const sorted = [...indices].sort((a, b) => a - b);
    if (!sorted.length) {
      return "";
    }
    const runs = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let k = 1; k < sorted.length; k++) {
      if (sorted[k] === prev + 1) {
        prev = sorted[k];
        continue;
      }
      runs.push([start, prev]);
      start = prev = sorted[k];
    }
    runs.push([start, prev]);
    return runs.map(([s, e]) => (s === e ? `p${s + 1}` : `p${s + 1}–p${e + 1}`)).join(", ");
  }

  // Enter scoped ("page slicer") mode for a document type. Jumps to the first
  // matching page only if the current page isn't already within the subset.
  setFilter(type) {
    this.activeFilter = type;
    const indices = this.filteredIndices;
    if (!indices.length) {
      this.activeFilter = null; // defensive: no pages of this type
      this.currentPageRef = this._pages[this.currentPageIndex]; // ensure reactivity
      return;
    }
    if (!indices.includes(this.currentPageIndex)) {
      this._setIndex(indices[0]);
    } else {
      // Even if we don't change the page index, update currentPageRef to trigger reactivity
      // when activeFilter changes.
      this.currentPageRef = this._pages[this.currentPageIndex];
    }
  }

  clearFilter() {
    this.activeFilter = null;
    // Update currentPageRef to trigger reactivity when filter is cleared.
    this.currentPageRef = this._pages[this.currentPageIndex];
  }

  goToDataFormPage(dataForm) {
    if (!dataForm?.pages) {
      return;
    }
    // match by uiId because multiple cards can have the same page
    let pageIx = this._pages.findIndex((page) => page.uiId === dataForm.pages[0]?.uiId);
    if (pageIx === -1) {
      // match by id as a fallback, the intake-review page has different uiIds for each form
      pageIx = this._pages.findIndex((page) => page.id === dataForm.pages[0]?.id);
    }
    if (pageIx !== -1) {
      this.goToPage(pageIx);
    }
  }

  // Arbitrary jump to an array index. Exits scoped mode — every explicit jump
  // (TOC "All pages", back-to-top, field highlight) returns to full page order.
  goToPage(num) {
    this.clearFilter();
    this._setIndex(num);
  }

  // Jump to an array index, preserving scoped mode when the target page is within
  // the active filter; otherwise exit scope. Used by field-highlight navigation so
  // a highlight inside the current type keeps the user in their filtered view.
  goToPagePreservingScope(num) {
    if (this.activeFilter !== null && this.filteredIndices.includes(num)) {
      this._setIndex(num);
    } else {
      this.goToPage(num);
    }
  }

  async onNextPage() {
    if (this.activeFilter === null) {
      if (this.currentPageIndex < this._pages.length - 1) {
        this._setIndex(this.currentPageIndex + 1);
      }
      return;
    }
    const indices = this.filteredIndices;
    const pos = indices.indexOf(this.currentPageIndex);
    if (pos !== -1 && pos < indices.length - 1) {
      this._setIndex(indices[pos + 1]);
    }
  }

  async onPrevPage() {
    if (this.activeFilter === null) {
      if (this.currentPageIndex > 0) {
        this._setIndex(this.currentPageIndex - 1);
      }
      return;
    }
    const indices = this.filteredIndices;
    const pos = indices.indexOf(this.currentPageIndex);
    if (pos > 0) {
      this._setIndex(indices[pos - 1]);
    }
  }
}
